/**
 * BM (Business Data Driven Model) Shared Types
 *
 * Branded types and interfaces for BM metadata.
 */

import type { TBmDiagramId, TBmPackageId } from "./bm-diagrams";

// Re-export TBmPackageId for convenience
export type { TBmPackageId };

// ============================================================================
// Branded Types (Type-safe IDs)
// ============================================================================

declare const __brand: unique symbol;
type Brand<T, B> = T & { [__brand]: B };

/** BM Class ID (branded i64) */
export type TBmClassId = Brand<number, "BmClassId">;

/** BM Field ID (branded i64) */
export type TBmFieldId = Brand<number, "BmFieldId">;

/** BM Enum Value ID (branded i64) */
export type TBmEnumValueId = Brand<number, "BmEnumValueId">;

// ============================================================================
// Enums
// ============================================================================

/** Type of BM class */
export type TBmClassType =
  | "primitive"
  | "concrete"
  | "extension"
  | "enum";

// ============================================================================
// Entities
// ============================================================================

/** BM Class entity */
export interface IBmClass {
  readonly id: TBmClassId;
  readonly stableId: string;
  readonly name: string;
  readonly classType: TBmClassType;
  readonly isAbstract: boolean;
  readonly packageId: TBmPackageId;
  readonly parentClassId: TBmClassId | null;
  readonly parentClassStableId: string | null;
  readonly description: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Field cardinality */
export interface ICardinality {
  readonly min: number;
  readonly max: number | null; // null = unbounded
}

/** BM Field entity */
export interface IBmField {
  readonly id: TBmFieldId;
  readonly stableId: string;
  readonly classId: TBmClassId;
  readonly name: string;
  readonly typeClassId: TBmClassId;
  readonly isReference: boolean;
  readonly cardinality: ICardinality;
  readonly description: string | null;
  readonly ordinal: number | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** BM Enum Value entity */
export interface IBmEnumValue {
  readonly id: TBmEnumValueId;
  readonly classId: TBmClassId;
  readonly value: string;
  readonly ordinal: number | null;
  readonly description: string | null;
  readonly createdAt: string;
}

/** Class with its fields (for detail view) */
export interface IBmClassWithFields extends IBmClass {
  readonly fields: readonly IBmField[];
  readonly enumValues: readonly IBmEnumValue[] | null;
}

/** Full graph data for VueFlow */
export interface IBmGraph {
  readonly classes: readonly IBmClass[];
  readonly fields: readonly IBmField[];
  readonly enumValues: readonly IBmEnumValue[];
}

// ============================================================================
// Request DTOs
// ============================================================================

/** Request to create a new class */
export interface ICreateBmClassRequest {
  readonly name: string;
  readonly classType: TBmClassType;
  readonly isAbstract?: boolean;
  readonly packageId: TBmPackageId;
  readonly parentClassId?: TBmClassId;
  readonly description?: string;
}

/** Request to update a class */
export interface IUpdateBmClassRequest {
  readonly name?: string;
  readonly classType?: TBmClassType;
  readonly isAbstract?: boolean;
  readonly packageId?: TBmPackageId;
  /** undefined = don't change, null = clear, TBmClassId = set */
  readonly parentClassId?: TBmClassId | null;
  /**
   * Описание можно очистить: undefined — не менять, null — стереть, строка —
   * записать (spec/features/business-model/classes.md, R08).
   */
  readonly description?: string | null;
}

/** Request to create a new field */
export interface ICreateBmFieldRequest {
  readonly name: string;
  readonly typeClassId: TBmClassId;
  readonly isReference?: boolean;
  readonly minCardinality?: number;
  readonly maxCardinality?: number | null;
  readonly description?: string;
  readonly ordinal?: number;
}

/** Request to update a field */
export interface IUpdateBmFieldRequest {
  readonly name?: string;
  readonly typeClassId?: TBmClassId;
  readonly isReference?: boolean;
  readonly minCardinality?: number;
  readonly maxCardinality?: number | null;
  readonly description?: string;
  readonly ordinal?: number;
}

/** Request to create an enum value */
export interface ICreateBmEnumValueRequest {
  readonly value: string;
  readonly ordinal?: number;
  readonly description?: string;
}

/** Request to update an enum value */
export interface IUpdateBmEnumValueRequest {
  readonly value?: string;
  readonly ordinal?: number;
  readonly description?: string;
}

/**
 * Размещение заводимого класса на диаграмме — часть того же действия
 * (spec/features/business-model/classes.md, R25).
 */
export interface IBmClassPlacement {
  readonly diagramId: TBmDiagramId;
  readonly x: number;
  readonly y: number;
  readonly width?: number | null;
}

/**
 * Заведение класса вместе с его полями, значениями перечисления и (если задана
 * диаграмма) размещением на ней — ОДНО действие.
 *
 * Норма: spec/features/business-model/classes.md, R25, R26 — сбой любой части
 * отклоняет действие целиком, класса с частью полей в модели не появляется.
 */
export interface ICreateBmClassWithContentRequest {
  readonly class: ICreateBmClassRequest;
  readonly fields: readonly ICreateBmClassFieldRequest[];
  readonly enumValues: readonly ICreateBmEnumValueRequest[];
  readonly placement?: IBmClassPlacement;
}

/**
 * Поле в составе заводимого класса.
 *
 * Отличие от {@link ICreateBmFieldRequest} — тип может быть не выбран: такое
 * поле НЕ отбрасывается молча, а отправляется продукту, который отвечает
 * названным отказом «у поля не выбран тип»
 * (spec/features/business-model/fields.md, R29; classes.md, R26).
 */
export interface ICreateBmClassFieldRequest
  extends Omit<ICreateBmFieldRequest, "typeClassId"> {
  readonly typeClassId: TBmClassId | null;
}

// ============================================================================
// Helper functions
// ============================================================================

/** Create a BmClassId from number */
export const toBmClassId = (id: number): TBmClassId => id as TBmClassId;

/** Create a BmFieldId from number */
export const toBmFieldId = (id: number): TBmFieldId => id as TBmFieldId;

/** Create a BmEnumValueId from number */
export const toBmEnumValueId = (id: number): TBmEnumValueId =>
  id as TBmEnumValueId;

/** Format cardinality as UML notation */
export const formatCardinality = (cardinality: ICardinality): string => {
  const { min, max } = cardinality;
  if (max === null) {
    return min === 0 ? "[0..*]" : `[${min}..*]`;
  }
  if (min === max) {
    return min === 1 ? "" : `[${min}]`; // [1..1] is default, don't show
  }
  return `[${min}..${max}]`;
};

/** Check if class type can have fields */
export const canHaveFields = (classType: TBmClassType): boolean =>
  classType === "concrete" ||
  classType === "extension";

/** Get class type display name */
export const getClassTypeDisplayName = (classType: TBmClassType, isAbstract?: boolean): string => {
  if (isAbstract === true && classType === "concrete") return "Abstract";
  switch (classType) {
    case "primitive":
      return "Primitive";
    case "concrete":
      return "Class";
    case "extension":
      return "Extension";
    case "enum":
      return "Enum";
  }
};

/** Get class type color for UI */
export const getClassTypeColor = (
  classType: TBmClassType,
  isAbstract?: boolean
): { bg: string; text: string } => {
  if (isAbstract === true && classType === "concrete") {
    return { bg: "#ffcdd2", text: "#c62828" };
  }
  switch (classType) {
    case "primitive":
      return { bg: "#e0e0e0", text: "#424242" };
    case "concrete":
      return { bg: "#fff9c4", text: "#f57f17" }; // yellow
    case "extension":
      return { bg: "#e0e0e0", text: "#424242" }; // gray
    case "enum":
      return { bg: "#c8e6c9", text: "#2e7d32" }; // green
  }
};
